import { cn } from "@/lib/utils";
import { compactNumber, elapsed, usd } from "../../lib/format";
import type { AgentSummary, RunStationSummary } from "../../types";
import { FlatTable } from "../flat-table";
import { SortHead } from "../sort-head";
import { ColorDot, ToneBadge } from "../status-badge";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

export const STATION_SORTS = [
  "runs",
  "failureRate",
  "median",
  "p90",
  "avgCost",
  "totalCost",
  "tokens",
] as const;
export type StationSort = (typeof STATION_SORTS)[number];

const VALUE: Record<StationSort, (row: RunStationSummary) => number | null> = {
  runs: (row) => row.runs,
  failureRate: (row) => row.failureRate,
  median: (row) => row.medianMs,
  p90: (row) => row.p90Ms,
  avgCost: (row) => row.avgCostUsd,
  totalCost: (row) => row.totalCostUsd,
  tokens: (row) => row.totalTokens,
};

const NONE = <span className="text-muted-foreground/60">—</span>;

/** Stations side by side over the runs the page's filters match. A row opens that station's runs. */
export function StationComparison({
  stations,
  agentById,
  sort,
  direction,
  onSort,
  onOpen,
  className,
}: {
  stations: RunStationSummary[];
  agentById: Map<string, AgentSummary>;
  sort: StationSort;
  direction: "asc" | "desc";
  onSort: (key: StationSort) => void;
  onOpen: (agentId: string) => void;
  className?: string;
}) {
  const sign = direction === "asc" ? 1 : -1;
  // Stations without a figure sink to the bottom either way, as the runs list does.
  const rows = [...stations].sort((a, b) => {
    const left = VALUE[sort](a);
    const right = VALUE[sort](b);
    if (left === null || right === null) return left === right ? b.runs - a.runs : left === null ? 1 : -1;
    return (left - right) * sign || b.runs - a.runs;
  });
  const head = (key: StationSort, label: string, hidden?: string) => (
    <SortHead
      label={label}
      active={sort === key}
      direction={direction}
      onSort={() => onSort(key)}
      className={hidden}
    />
  );

  return (
    <FlatTable className={className}>
      <TableHeader>
        <TableRow>
          <TableHead>Station</TableHead>
          {head("runs", "Runs")}
          {head("failureRate", "Failure rate")}
          {head("median", "Median time", "hidden md:table-cell")}
          {head("p90", "p90 time", "hidden lg:table-cell")}
          {head("avgCost", "Avg cost", "hidden md:table-cell")}
          {head("totalCost", "Total cost", "hidden sm:table-cell")}
          {head("tokens", "Tokens", "hidden xl:table-cell")}
          <TableHead className="hidden xl:table-cell">Verdicts</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const agent = agentById.get(row.agentId);
          const verdicts = Object.entries(row.verdicts);
          const failedRuns = row.failed + row.expired;
          return (
            <TableRow key={row.agentId} className="cursor-pointer" onClick={() => onOpen(row.agentId)}>
              <TableCell className="w-full whitespace-nowrap">
                <span className="flex items-center gap-2">
                  {agent && <ColorDot color={agent.station.color} />}
                  {agent?.name ?? row.agentId}
                  {row.active > 0 && <ToneBadge tone="sky">{row.active} active</ToneBadge>}
                </span>
              </TableCell>
              <TableCell className="text-right text-xs tabular-nums">{row.runs.toLocaleString()}</TableCell>
              <TableCell
                className={cn("text-right text-xs tabular-nums", failedRuns > 0 && "text-destructive")}
                title={`${failedRuns} of ${row.completed + failedRuns} finished runs failed or expired`}
              >
                {row.failureRate === null ? NONE : `${Math.round(row.failureRate * 100)}%`}
              </TableCell>
              <TableCell className="hidden text-right text-xs whitespace-nowrap text-muted-foreground tabular-nums md:table-cell">
                {row.medianMs === null ? NONE : elapsed(row.medianMs)}
              </TableCell>
              <TableCell className="hidden text-right text-xs whitespace-nowrap text-muted-foreground tabular-nums lg:table-cell">
                {row.p90Ms === null ? NONE : elapsed(row.p90Ms)}
              </TableCell>
              <TableCell className="hidden text-right text-xs tabular-nums md:table-cell">
                {row.avgCostUsd === null ? NONE : usd(row.avgCostUsd)}
              </TableCell>
              <TableCell className="hidden text-right text-xs tabular-nums sm:table-cell">
                {row.totalCostUsd === null ? NONE : usd(row.totalCostUsd)}
              </TableCell>
              <TableCell
                className="hidden text-right text-xs text-muted-foreground tabular-nums xl:table-cell"
                title={row.totalTokens === null ? undefined : `${row.totalTokens.toLocaleString()} tokens`}
              >
                {row.totalTokens === null ? NONE : compactNumber(row.totalTokens)}
              </TableCell>
              <TableCell className="hidden xl:table-cell">
                {verdicts.length === 0 ? (
                  NONE
                ) : (
                  <span className="flex flex-wrap gap-1">
                    {verdicts.slice(0, 3).map(([verdict, count]) => (
                      <ToneBadge key={verdict} tone="violet">
                        {verdict} {count}
                      </ToneBadge>
                    ))}
                    {verdicts.length > 3 && (
                      <span className="text-xs text-muted-foreground">+{verdicts.length - 3}</span>
                    )}
                  </span>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </FlatTable>
  );
}
